import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, ArrowLeft, FilePdf, Trash, PaperPlaneTilt } from "@phosphor-icons/react";
import { format, addDays } from "date-fns";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLE = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-[#F5A623]/15 text-[#F5A623]",
  deposit_paid: "bg-[#9D5CFF]/15 text-[#C9A8FF]",
  settled: "bg-[#A9C97E]/15 text-[#A9C97E]",
  paid: "bg-[#A9C97E]/15 text-[#A9C97E]",
  void: "bg-[#F87171]/10 text-[#F87171]",
};
const STATUS_LABELS = { draft: "Draft", sent: "Sent", deposit_paid: "Deposit paid", settled: "Settled", paid: "Settled", void: "Void" };
const NEXT_STATUS = { draft: ["sent", "Mark sent"], sent: ["deposit_paid", "Mark deposit paid"], deposit_paid: ["settled", "Mark settled"] };

const emptyLine = () => ({ _k: crypto.randomUUID(), description: "", quantity: 1, rate: "" });

function compute(lines, discount, paid, vatRate) {
  const subtotal = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0);
  const total = subtotal - (Number(discount) || 0);
  const vat = vatRate ? (total * vatRate) / (100 + vatRate) : 0;
  const balance = total - (Number(paid) || 0);
  return { subtotal, total, vat, balance };
}

export default function ClientInvoices() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [invoices, setInvoices] = useState(null);
  const [preset, setPreset] = useState(null);
  const [editing, setEditing] = useState(null); // null = list, {} = builder
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/client-invoices").then((r) => setInvoices(r.data)).catch(() => toast.error("Failed to load invoices"));
  useEffect(() => {
    load();
    api.get("/client-invoices/preset").then((r) => setPreset(r.data)).catch(() => {});
  }, []);

  const blank = () => ({
    client_name: "", bill_to: "", ship_to: "",
    date: format(new Date(), "yyyy-MM-dd"),
    due_date: format(addDays(new Date(), preset?.payment_terms_days ?? 14), "yyyy-MM-dd"),
    payment_terms: preset?.payment_terms_days ? `${preset.payment_terms_days} days` : "",
    po_number: "", lines: [emptyLine()], discount: "", deposit_required: "", amount_paid: "", amount_paid_date: "",
    notes: "", terms: preset?.invoice_terms || "", banking: preset?.banking || {}, status: "draft", function_date: "",
  });

  const openNew = (pre = {}) => setEditing({ ...blank(), ...pre, _new: true });

  // Function handoff: /client-invoices?new=1&client=…&fdate=…&fid=… opens a prefilled builder
  useEffect(() => {
    if (params.get("new") === "1" && preset) {
      openNew({
        client_name: params.get("client") || "",
        function_date: params.get("fdate") || "",
        function_id: params.get("fid") || null,
      });
    }
  }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const openEdit = (inv) => setEditing({ ...inv, lines: inv.lines.map((l) => ({ ...l, _k: l.id || crypto.randomUUID() })), banking: inv.banking || preset?.banking || {}, terms: inv.terms || preset?.invoice_terms || "" });

  const save = async () => {
    if (!editing.client_name.trim()) return toast.error("Client name required");
    if (!editing.lines.some((l) => l.description.trim())) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = {
        ...editing,
        lines: editing.lines.filter((l) => l.description.trim()).map((l) => ({ description: l.description, quantity: Number(l.quantity) || 0, rate: Number(l.rate) || 0 })),
        discount: Number(editing.discount) || 0,
        deposit_required: Number(editing.deposit_required) || 0,
        amount_paid: Number(editing.amount_paid) || 0,
        amount_paid_date: editing.amount_paid_date || null,
        due_date: editing.due_date || null,
        function_date: editing.function_date || null,
      };
      delete payload._new;
      if (editing._new) await api.post("/client-invoices", payload);
      else await api.put(`/client-invoices/${editing.id}`, payload);
      toast.success(editing._new ? "Invoice created" : "Invoice updated");
      api.get("/client-invoices/preset").then((r) => setPreset(r.data)).catch(() => {}); // keep next-number preview in sync
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (inv) => {
    try {
      await api.delete(`/client-invoices/${inv.id}`);
      toast.success(`Invoice ${inv.invoice_no} deleted`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  const markStatus = async (inv, status) => {
    try {
      const patch = { ...inv, status };
      if (status === "deposit_paid" && (inv.deposit_required || 0) > (inv.amount_paid || 0)) {
        patch.amount_paid = inv.deposit_required;
        patch.amount_paid_date = inv.amount_paid_date || format(new Date(), "yyyy-MM-dd");
      }
      await api.put(`/client-invoices/${inv.id}`, patch);
      toast.success(`${inv.invoice_no} → ${STATUS_LABELS[status]}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Status update failed"));
    }
  };

  if (!can("view_financials")) return <p className="text-muted-foreground" data-testid="ci-no-perm">You don't have permission to view client invoices.</p>;
  if (invoices === null) return <p className="text-muted-foreground">Loading…</p>;

  if (editing) {
    return <Builder inv={editing} setInv={setEditing} preset={preset} saving={saving} onSave={save} onBack={() => setEditing(null)} />;
  }

  return (
    <div className="space-y-6" data-testid="client-invoices-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold">Client Invoices</h1>
          <p className="text-muted-foreground mt-1">Outbound invoices for functions &amp; events — company, banking and terms preset per venue in Settings</p>
        </div>
        <Button onClick={() => openNew()} data-testid="ci-new-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
          <Plus size={16} className="mr-1" /> New invoice
        </Button>
      </div>

      <Card className="border-border shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="ci-table">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Invoice #</th>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium text-right">Total</th>
                <th className="px-4 py-3 font-medium text-right">Paid</th>
                <th className="px-4 py-3 font-medium text-right">Balance</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0 hover:bg-accent/40 cursor-pointer" data-testid={`ci-row-${inv.id}`} onClick={() => openEdit(inv)}>
                  <td className="px-4 py-2.5 font-medium">{inv.invoice_no}</td>
                  <td className="px-4 py-2.5">{inv.client_name}</td>
                  <td className="px-4 py-2.5 num">{inv.date}</td>
                  <td className="px-4 py-2.5 num text-right">{zar(inv.total)}</td>
                  <td className="px-4 py-2.5 num text-right">{zar(inv.amount_paid)}</td>
                  <td className="px-4 py-2.5 num text-right font-medium">{zar(inv.balance_due)}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={STATUS_STYLE[inv.status]} data-testid={`ci-status-${inv.id}`}>{STATUS_LABELS[inv.status] || inv.status}</Badge>
                    {(inv.deposit_required || 0) > 0 && !["settled", "paid", "void"].includes(inv.status) && (
                      <Badge className={`ml-1.5 ${(inv.amount_paid || 0) >= inv.deposit_required ? "bg-[#A9C97E]/15 text-[#A9C97E]" : "bg-[#F5A623]/15 text-[#F5A623]"}`}
                        data-testid={`ci-deposit-${inv.id}`}>
                        {(inv.amount_paid || 0) >= inv.deposit_required ? "Deposit ✓" : "Deposit pending"}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end items-center">
                      {NEXT_STATUS[inv.status] && (
                        <Button size="sm" variant="outline" className="text-xs h-7" data-testid={`ci-mark-${inv.id}`}
                          onClick={() => markStatus(inv, NEXT_STATUS[inv.status][0])}>
                          {NEXT_STATUS[inv.status][1]}
                        </Button>
                      )}
                      <a href={`${API_BASE}/client-invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`ci-pdf-${inv.id}`}>
                        <Button size="sm" variant="ghost"><FilePdf size={16} /></Button>
                      </a>
                      <Button size="sm" variant="ghost" className="text-destructive" data-testid={`ci-delete-${inv.id}`} onClick={() => remove(inv)}><Trash size={15} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="ci-empty">
                  No client invoices yet — create one for your next function or event.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Builder({ inv, setInv, preset, saving, onSave, onBack }) {
  const vatRate = preset?.vat_rate ?? 15;
  const calc = useMemo(() => compute(inv.lines, inv.discount, inv.amount_paid, vatRate), [inv.lines, inv.discount, inv.amount_paid, vatRate]);
  const set = (k, v) => setInv({ ...inv, [k]: v });
  const setLine = (i, patch) => setInv({ ...inv, lines: inv.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const setBank = (k, v) => setInv({ ...inv, banking: { ...inv.banking, [k]: v } });
  const invNo = inv.invoice_no || preset?.next_invoice_no || "—";

  return (
    <div className="space-y-5" data-testid="ci-builder">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="ci-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
          <h1 className="font-heading text-2xl font-bold">{inv._new ? `New invoice · ${invNo}` : `Invoice ${inv.invoice_no}`}</h1>
        </div>
        <div className="flex gap-2">
          {!inv._new && (
            <a href={`${API_BASE}/client-invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" data-testid="ci-builder-pdf">
              <Button variant="outline"><FilePdf size={16} className="mr-1" /> PDF</Button>
            </a>
          )}
          <Button onClick={onSave} disabled={saving} data-testid="ci-save" className="bg-primary hover:bg-[#8B5CF6] text-white">
            <PaperPlaneTilt size={15} className="mr-1" /> {saving ? "Saving…" : inv._new ? "Create invoice" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-5">
          <Card className="border-border shadow-none">
            <CardContent className="pt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Client name *</Label>
                  <Input data-testid="ci-client-name" value={inv.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder="e.g. Smith Wedding — Sarah Smith" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Bill To (address / contact details)</Label>
                  <Textarea rows={3} data-testid="ci-bill-to" value={inv.bill_to} onChange={(e) => set("bill_to", e.target.value)} placeholder={"Sarah Smith\nsarah@example.com\n082 555 0100"} />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Ship To (optional)</Label>
                  <Textarea rows={2} data-testid="ci-ship-to" value={inv.ship_to} onChange={(e) => set("ship_to", e.target.value)} />
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" data-testid="ci-date" value={inv.date} onChange={(e) => set("date", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Due date</Label><Input type="date" data-testid="ci-due-date" value={inv.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Payment terms</Label><Input data-testid="ci-payment-terms" value={inv.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="e.g. 14 days" /></div>
                <div className="space-y-1.5"><Label className="text-xs">PO number</Label><Input data-testid="ci-po" value={inv.po_number} onChange={(e) => set("po_number", e.target.value)} /></div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Function / event date (optional)</Label>
                  <Input type="date" data-testid="ci-function-date" value={inv.function_date || ""} onChange={(e) => set("function_date", e.target.value)} />
                  <p className="text-[11px] text-muted-foreground">Set this and the function appears on the Functions calendar; its revenue is recognized on this date (not when the deposit landed).</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-none">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold text-sm">Line items <span className="text-muted-foreground font-normal">(rates are VAT-inclusive)</span></p>
                <Button size="sm" variant="outline" data-testid="ci-add-line" onClick={() => set("lines", [...inv.lines, emptyLine()])}><Plus size={14} className="mr-1" /> Add row</Button>
              </div>
              <div className="grid grid-cols-[1fr_80px_110px_90px_36px] gap-2 text-xs text-muted-foreground px-0.5">
                <span>Description</span><span>Qty</span><span>Rate (R)</span><span className="text-right">Amount</span><span />
              </div>
              {inv.lines.map((l, i) => (
                <div key={l._k || l.id || i} className="grid grid-cols-[1fr_80px_110px_90px_36px] gap-2 items-center" data-testid={`ci-line-${i}`}>
                  <Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} data-testid={`ci-line-desc-${i}`} placeholder="e.g. Canapés per person" />
                  <Input type="number" step="any" min="0" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} data-testid={`ci-line-qty-${i}`} />
                  <Input type="number" step="any" min="0" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} data-testid={`ci-line-rate-${i}`} />
                  <span className="text-sm text-right num" data-testid={`ci-line-amount-${i}`}>{zar((Number(l.quantity) || 0) * (Number(l.rate) || 0))}</span>
                  <Button size="sm" variant="ghost" className="text-destructive" data-testid={`ci-line-del-${i}`}
                    onClick={() => set("lines", inv.lines.filter((_, j) => j !== i))} disabled={inv.lines.length === 1}><Trash size={14} /></Button>
                </div>
              ))}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t pt-3">
                <div className="space-y-1.5"><Label className="text-xs">Discount (R)</Label><Input type="number" step="any" min="0" data-testid="ci-discount" value={inv.discount} onChange={(e) => set("discount", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Deposit required (R)</Label><Input type="number" step="any" min="0" data-testid="ci-deposit-required" value={inv.deposit_required} onChange={(e) => set("deposit_required", e.target.value)} placeholder="Stated on invoice" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Amount paid / deposit (R)</Label><Input type="number" step="any" min="0" data-testid="ci-amount-paid" value={inv.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Deposit received on</Label><Input type="date" data-testid="ci-paid-date" value={inv.amount_paid_date || ""} onChange={(e) => set("amount_paid_date", e.target.value)} /></div>
              </div>
              <div className="flex justify-between text-sm border-t pt-3">
                <span className="text-muted-foreground">Total (incl. VAT {vatRate}% of {zar(calc.vat)})</span>
                <span className="font-semibold num" data-testid="ci-total">{zar(calc.total)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Balance due</span>
                <span className="font-bold num text-[#C9A8FF]" data-testid="ci-balance">{zar(calc.balance)}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-none">
            <CardContent className="pt-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Notes for client</Label>
                <Textarea rows={2} data-testid="ci-notes" value={inv.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Bank</Label><Input data-testid="ci-bank-name" value={inv.banking.bank_name || ""} onChange={(e) => setBank("bank_name", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Account name</Label><Input data-testid="ci-account-name" value={inv.banking.account_name || ""} onChange={(e) => setBank("account_name", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Account number</Label><Input data-testid="ci-account-number" value={inv.banking.account_number || ""} onChange={(e) => setBank("account_number", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Branch code</Label><Input data-testid="ci-branch-code" value={inv.banking.branch_code || ""} onChange={(e) => setBank("branch_code", e.target.value)} /></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Terms</Label>
                <Textarea rows={2} data-testid="ci-terms" value={inv.terms} onChange={(e) => set("terms", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <Select value={inv.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="ci-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="deposit_paid">Deposit paid</SelectItem>
                      <SelectItem value="settled">Settled</SelectItem>
                      <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <InvoicePreview inv={inv} calc={calc} preset={preset} invNo={invNo} vatRate={vatRate} />
      </div>
    </div>
  );
}

function InvoicePreview({ inv, calc, preset, invNo, vatRate }) {
  const [logoTs] = useState(Date.now());
  return (
    <div className="bg-white text-[#1A1A1A] rounded-lg shadow-xl p-8 text-[13px] leading-relaxed self-start sticky top-24" data-testid="ci-preview">
      <div className="flex justify-between items-start">
        <div>
          {preset?.has_logo && <img src={`${API_BASE}/settings/logo?ts=${logoTs}`} alt="logo" className="h-14 object-contain mb-2" data-testid="ci-preview-logo" />}
          <p className="font-bold text-[15px]">{preset?.business_name || preset?.venue_name}</p>
          <div className="text-[11px] text-[#666] whitespace-pre-line">
            {[preset?.business_reg && `Reg: ${preset.business_reg}`, preset?.business_vat && `VAT: ${preset.business_vat}`,
              preset?.business_address, preset?.business_email, preset?.business_phone].filter(Boolean).join("\n")}
          </div>
        </div>
        <div className="text-right">
          <p className="font-heading font-black text-3xl text-[#4A1D96] tracking-tight">INVOICE</p>
          <p className="font-bold" data-testid="ci-preview-no">{invNo}</p>
          <div className="mt-3 bg-[#F4F1FA] rounded px-3 py-2 text-left space-y-1 min-w-[180px]">
            <div className="flex justify-between gap-4"><span className="text-[10px] text-[#666]">DATE</span><span className="font-semibold" data-testid="ci-preview-date">{inv.date}</span></div>
            <div className="flex justify-between gap-4"><span className="text-[10px] text-[#666]">DUE DATE</span><span className="font-semibold">{inv.due_date || "—"}</span></div>
            {inv.po_number && <div className="flex justify-between gap-4"><span className="text-[10px] text-[#666]">PO NUMBER</span><span className="font-semibold">{inv.po_number}</span></div>}
            <div className="flex justify-between gap-4"><span className="text-[10px] text-[#666]">BALANCE DUE</span><span className="font-bold" data-testid="ci-preview-balance">{zar(calc.balance)}</span></div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <p className="font-bold text-[11px] text-[#4A1D96] uppercase tracking-wide">Bill To</p>
        <p className="font-semibold">{inv.client_name || "Client name"}</p>
        <p className="text-[#666] whitespace-pre-line">{inv.bill_to}</p>
      </div>

      <table className="w-full mt-6">
        <thead>
          <tr className="bg-[#1A1A1A] text-white text-left">
            <th className="px-3 py-2 font-semibold">Item</th>
            <th className="px-3 py-2 font-semibold text-right">Quantity</th>
            <th className="px-3 py-2 font-semibold text-right">Rate</th>
            <th className="px-3 py-2 font-semibold text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {inv.lines.map((l, i) => (
            <tr key={l._k || l.id || i} className={i % 2 === 0 ? "bg-[#FAFAFA]" : ""}>
              <td className="px-3 py-2">{l.description || "—"}</td>
              <td className="px-3 py-2 text-right">{Number(l.quantity) || 0}</td>
              <td className="px-3 py-2 text-right">{(Number(l.rate) || 0).toFixed(2)}</td>
              <td className="px-3 py-2 text-right">{((Number(l.quantity) || 0) * (Number(l.rate) || 0)).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 ml-auto w-64 space-y-1">
        <div className="flex justify-between"><span className="text-[#666]">Subtotal</span><span data-testid="ci-preview-subtotal">{zar(calc.subtotal)}</span></div>
        {Number(inv.discount) > 0 && <div className="flex justify-between"><span className="text-[#666]">Discount</span><span>− {zar(Number(inv.discount))}</span></div>}
        <div className="flex justify-between font-bold"><span>Total (VAT incl.)</span><span>{zar(calc.total)}</span></div>
        <div className="flex justify-between text-[11px]"><span className="text-[#666]">Includes VAT {vatRate}% of</span><span>{zar(calc.vat)}</span></div>
        {Number(inv.deposit_required) > 0 && <div className="flex justify-between font-medium" data-testid="ci-preview-deposit"><span className="text-[#4A1D96]">Deposit required</span><span>{zar(Number(inv.deposit_required))}</span></div>}
        <div className="flex justify-between"><span className="text-[#666]">Amount Paid</span><span>− {zar(Number(inv.amount_paid) || 0)}</span></div>
        <div className="flex justify-between bg-[#4A1D96] text-white font-bold px-2 py-1 rounded"><span>BALANCE DUE</span><span>{zar(calc.balance)}</span></div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-6">
        <div>
          {(inv.banking?.bank_name || inv.banking?.account_number) && (
            <>
              <p className="font-bold text-[11px] text-[#4A1D96] uppercase tracking-wide">Banking Details</p>
              <p className="whitespace-pre-line text-[12px]">{[inv.banking.account_name, inv.banking.bank_name,
                inv.banking.account_number && `Acc: ${inv.banking.account_number}`,
                inv.banking.branch_code && `Branch: ${inv.banking.branch_code}`].filter(Boolean).join("\n")}</p>
            </>
          )}
        </div>
        <div>
          {inv.terms && (
            <>
              <p className="font-bold text-[11px] text-[#4A1D96] uppercase tracking-wide">Terms</p>
              <p className="text-[11px] text-[#666] whitespace-pre-line">{inv.terms}</p>
            </>
          )}
        </div>
      </div>
      {inv.notes && <p className="mt-4 text-[11px] italic text-[#666] text-right">Notes: {inv.notes}</p>}
    </div>
  );
}
